import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

import { AnswerChangeSchema } from '../answer-change/answer-change';
import { InterviewAccessSchema } from '../interview-access/interview-access';
import { InterviewSchema } from '../interview/interview';
import { VerificationCaseKindSchema } from '../verification-case-kind/verification-case-kind';
import { VerificationCaseStatusSchema } from '../verification-case-status/verification-case-status';
import { VerificationDecisionReasonSchema } from '../verification-decision/verification-decision';
import {
  isMemberSubmittableStep,
  VerificationStepKeySchema,
} from '../verification-step-key/verification-step-key';
import {
  isResolvedStepStatus,
  isSatisfiedStepStatus,
  VerificationStepStatusSchema,
} from '../verification-step-status/verification-step-status';

/**
 * One materialized step.
 *
 * `mediaAssetId` is an **id**, never a URL and never a storage key (N12): the
 * client asks `media` for a short-lived, authorized mint when it needs to show
 * the image, and that mint is audit-logged for the evidence kinds.
 */
export const VerificationStepSchema = z
  .object({
    key: VerificationStepKeySchema,
    status: VerificationStepStatusSchema,
    mediaAssetId: UuidSchema.nullable(),
    submittedAt: z.iso.datetime().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    /** A reviewer's note *to the member* — what to fix. Not the private interview notes. */
    note: z.string().nullable(),
  })
  .strict();

export type VerificationStep = z.infer<typeof VerificationStepSchema>;

/**
 * The two things a case is made of, by kind: an **initial** case has steps and
 * no answer changes; a **re-review** has answer changes and no steps (D6). A
 * case that has nothing to judge is not a case, whichever kind it is.
 */
export function refineCaseContents(
  kase: {
    kind: z.infer<typeof VerificationCaseKindSchema>;
    steps: readonly unknown[];
    answerChanges: readonly unknown[];
  },
  ctx: z.RefinementCtx,
): void {
  if (kase.kind === 'initial' && kase.steps.length === 0) {
    ctx.addIssue({
      code: 'too_small',
      minimum: 1,
      inclusive: true,
      origin: 'array',
      path: ['steps'],
      message: 'An initial case needs at least one step.',
    });
  }
  if (kase.kind === 're_review' && kase.answerChanges.length === 0) {
    ctx.addIssue({
      code: 'too_small',
      minimum: 1,
      inclusive: true,
      origin: 'array',
      path: ['answerChanges'],
      message: 'A re-review needs at least one edited answer.',
    });
  }
}

/**
 * One verification attempt, as the **member's** own screen reads it.
 *
 * Two absences are deliberate and are asserted by the spec rather than left to a
 * mapper's discretion: there is no `userId` (the case is reached from the token,
 * never enumerated) and no `decidedByUserId` — a member is told *what* was
 * decided and *why*, never which member of staff decided it.
 *
 * `steps` is exactly what materialization produced for this member's gender, so
 * a gender-waived step simply is not in the array and the checklist that renders
 * it needs no branch (U15). A `re_review` materializes none: its
 * `answerChanges` are what is under review (D6).
 */
export const VerificationCaseSchema = z
  .object({
    id: UuidSchema,
    kind: VerificationCaseKindSchema,
    status: VerificationCaseStatusSchema,
    /** 1 for a first attempt of this kind; a resubmission after a rejection opens attempt 2. */
    attemptNumber: z.number().int().positive(),
    steps: z.array(VerificationStepSchema),
    /** The edited answers a re-review judges; empty on an initial case. */
    answerChanges: z.array(AnswerChangeSchema),
    /** Present only when the case materialized an interview step and one was requested. */
    interview: InterviewSchema.nullable(),
    /**
     * What stands between the member and the calendar (D9a) — stated by the
     * server for every case, so a member whose gender waived the step reads a
     * quiet `feeEnabled: false` rather than a missing field.
     */
    interviewAccess: InterviewAccessSchema,
    decisionReasonCode: VerificationDecisionReasonSchema.nullable(),
    decisionNote: z.string().nullable(),
    submittedAt: z.iso.datetime().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .strict()
  .superRefine(refineCaseContents);

export type VerificationCase = z.infer<typeof VerificationCaseSchema>;

/**
 * How many steps are still waiting on somebody — what the pending screen counts.
 *
 * A **waived** step is not outstanding (a reviewer decided not to ask), but a
 * **failed** one is: it has to be redone.
 */
export function countOutstandingSteps(steps: readonly VerificationStep[]): number {
  return steps.filter((step) => !isSatisfiedStepStatus(step.status)).length;
}

/**
 * Whether the member has done everything the case needs from them.
 *
 * Only **member-submittable** steps count. `face_match` materializes as
 * `pending` and stays there until the server computes it, so waiting on it would
 * deadlock every submission; the `interview` is booked through its own route and
 * staff schedule it, so waiting on it would stop a member submitting evidence
 * before an appointment exists.
 *
 * A step a reviewer already **resolved** counts as sent — a reviewer who passed
 * the ID and asked only for a new selfie must not be told the ID is missing —
 * except when it was `failed`, which is precisely the request to send it again.
 *
 * A case with no member-submittable step at all is **not** submittable: there
 * would be nothing for the member to have done, and answering otherwise would
 * let a submit move a case the member never touched.
 */
export function isCaseSubmittable(steps: readonly VerificationStep[]): boolean {
  const mine = steps.filter((step) => isMemberSubmittableStep(step.key));

  if (mine.length === 0) return false;

  return mine.every(
    (step) =>
      step.status === 'submitted' ||
      (isResolvedStepStatus(step.status) && step.status !== 'failed'),
  );
}

/**
 * Whether a reviewer has accepted every piece of evidence the member sent —
 * what opens the interview (F4).
 *
 * The interview is the expensive step (a human's booked hour, and the member's
 * money when the fee is on), so it waits for the cheap one: nobody pays for,
 * and no reviewer sits through, an interview with a member whose ID was about
 * to be refused. Only **member-submittable** steps count; `submitted` is sent,
 * not judged, and `failed` is a resubmission owed. A case whose gender waived
 * every evidence step has nothing standing before the interview.
 */
export function isEvidenceApproved(
  steps: readonly Pick<VerificationStep, 'key' | 'status'>[],
): boolean {
  return steps.every(
    (step) => !isMemberSubmittableStep(step.key) || isSatisfiedStepStatus(step.status),
  );
}
